import React, { useState, useEffect } from 'react';
import { ResponsiveSankey } from '@nivo/sankey';
import ChartFrame from './ChartFrame';
import CashFlowBreakdown from './CashFlowBreakdown';

interface SankeyNode {
  id: string;
}

interface SankeyLink {
  source: string;
  target: string;
  value: number;
}

interface Props {
  nodes: SankeyNode[];
  links: SankeyLink[];
}

// High-luminance palette tuned for the dark chart background — every hue
// stays readable when used for node fills, link gradients, and label anchors.
const COLORS = [
  '#60a5fa', '#34d399', '#fbbf24', '#f87171',
  '#a78bfa', '#f472b6', '#22d3ee', '#a3e635',
  '#fb923c', '#e879f9', '#facc15', '#4ade80',
];

// d3-sankey (used by @nivo/sankey) throws "circular link" and crashes if the
// links form a cycle. Detect that up front so a bad payload falls back to the
// data table instead of tripping the app's ErrorBoundary.
const hasCycle = (links: SankeyLink[]): boolean => {
  const adjacency: Record<string, string[]> = {};
  for (const { source, target } of links) {
    (adjacency[source] ||= []).push(target);
  }
  const visited = new Set<string>();
  const stack = new Set<string>();

  const visit = (node: string): boolean => {
    if (stack.has(node)) return true;
    if (visited.has(node)) return false;
    visited.add(node);
    stack.add(node);
    for (const next of adjacency[node] || []) {
      if (visit(next)) return true;
    }
    stack.delete(node);
    return false;
  };

  return Object.keys(adjacency).some(visit);
};

const CashFlowChart: React.FC<Props> = ({ nodes, links }) => {
  const cyclic = hasCycle(links);
  const [showRaw, setShowRaw] = useState(false);

  // A Sankey needs more horizontal room than a phone or tablet offers, so up to
  // tablet width (≤1024px) we swap it for the top-to-bottom "Money In / Money
  // Out" breakdown; only desktop keeps the Sankey. Held in state with a
  // matchMedia listener so resizing / rotating re-renders the right view.
  const [useBreakdown, setUseBreakdown] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => setUseBreakdown(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  if (!links.length) {
    return (
      <div className="chart-wrapper">
        <p className="chart-empty">No cash flow data available.</p>
      </div>
    );
  }

  const renderChart = (fullscreen: boolean) => {
    if (useBreakdown) {
      return <CashFlowBreakdown links={links} />;
    }
    return (
      <div style={{ height: fullscreen ? '100%' : 440 }}>
        <ResponsiveSankey
          data={{ nodes, links }}
          margin={{ top: 16, right: 150, bottom: 16, left: 120 }}
          align="justify"
          colors={COLORS}
          nodeOpacity={1}
          nodeThickness={20}
          nodeInnerPadding={4}
          nodeSpacing={20}
          nodeBorderWidth={0}
          nodeBorderRadius={3}
          linkOpacity={0.65}
          linkHoverOpacity={0.9}
          linkBlendMode="normal"
          enableLinkGradient={true}
          labelPosition="outside"
          labelOrientation="horizontal"
          labelPadding={12}
          labelTextColor="#f8fafc"
          theme={{
            labels: { text: { fontSize: 12, fontWeight: 600 } },
            tooltip: { container: { background: '#1e293b', color: '#f8fafc' } },
          }}
        />
      </div>
    );
  };

  const renderTable = () => (
    <table className="chart-data-table">
      <thead>
        <tr><th>From</th><th>To</th><th>Amount</th></tr>
      </thead>
      <tbody>
        {links.map((l, i) => (
          <tr key={i}>
            <td>{l.source}</td>
            <td>{l.target}</td>
            <td>${l.value.toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <ChartFrame
      title="Cash Flow"
      showRaw={showRaw}
      onToggle={setShowRaw}
      chartDisabled={cyclic && !useBreakdown}
      renderChart={renderChart}
      renderTable={renderTable}
    />
  );
};

export default CashFlowChart;
